import { fontStyles, Text } from 'ui';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useSearchParams } from 'hooks';
import PromotionsListTable from 'components/Promotions/PromotionsListTable';
import { useEffect, useState } from 'react';
import { Stack } from '@koob/margaret';
import { KoobCreateButton, KoobInputSegmented } from '@koob/ui';
import { constantCase, decodeId, ProductTypeEnum } from 'utils';
import { isEqual } from 'lodash';
import { useAsync } from 'react-async';
import { getPromotions, getPromotionStateCounts } from 'api/node/promotions';
import { PER_PAGE_FOR_CATALOG } from 'constants';
import { PromotionStateEnum } from '@koob/enums';

export const CustomNavLink = styled(Stack).attrs({
  as: Link,
  gutterSize: 0.5,
  alignY: 'center',
})`
  ${fontStyles.bodySmall}

  color: ${({ theme }) => theme.textLight};
  padding: ${({ theme }) => theme.spacing(0.5)}
    ${({ theme }) => theme.spacing()};
  text-decoration: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  transition:
    background 150ms ease,
    color 150ms ease;
  margin-top: 8px;

  &:hover {
    background-color: ${({ theme }) => theme.sidebarBackgroundHoverColor};
    color: ${({ theme }) => theme.primary};
  }

  &.active {
    color: ${({ theme }) => theme.primary};
  }

  &:hover,
  &.active {
    color: ${({ theme }) => theme.primary};
    background-color: ${({ theme }) => theme.sidebarBackgroundHoverColor};
  }

  svg {
    font-size: 1.5rem;
  }
`;

export const SectionWrapper = styled.section`
  margin-block-start: ${({ theme }) => theme.spacing(1.5)};
  font-size: 1rem;
  position: relative;
`;

export const LinkSectionWrapper = styled.div`
  margin-block-start: ${({ theme }) => theme.spacing(1.5)};
  flex: 0 0 200px;
  margin-right: 10px;
`;

const PromotionsList = () => {
  const { t } = useTranslation(['promotions', 'hotel', 'misc']);
  const [searchParams] = useSearchParams();
  const { hotelId } = useParams();
  const theme = useTheme();
  const [promotionState, setPromotionState] = useState(
    PromotionStateEnum.OPERATIONAL,
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_FOR_CATALOG);

  const sortParams = { ...searchParams };

  const { data: byStateTotal } = useAsync({
    promiseFn: getPromotionStateCounts,
    productId: decodeId(hotelId),
    kind: ProductTypeEnum.HOTEL,
  });

  const { data: promotions, isLoading } = useAsync({
    promiseFn: getPromotions,
    productId: decodeId(hotelId),
    kind: ProductTypeEnum.HOTEL,
    state: promotionState,
    column: sortParams?.order,
    direction: sortParams?.direction && constantCase(sortParams.direction),
    page,
    perPage,
    watchFn: (props, prevProps) => {
      return (
        !isEqual(props.state, prevProps.state) ||
        !isEqual(props.direction, prevProps.direction) ||
        !isEqual(props.column, prevProps.column) ||
        !isEqual(props.page, prevProps.page) ||
        !isEqual(props.perPage, prevProps.perPage)
      );
    },
  });

  useEffect(() => {
    setPage(1);
  }, [promotionState, sortParams.order, sortParams.direction]);

  const headers = [
    { key: 'kind', label: t('dealType'), width: '14%', sortable: true },
    { key: 'name', label: t('promotionName'), width: '20%', sortable: true },
    {
      key: 'minNumberOfRooms',
      label: t('minRooms'),
      width: '12%',
    },
    {
      key: 'benefit',
      label: t('benefits'),
      width: '12%',
    },
    {
      key: 'status',
      label: t('status'),
      width: '12%',
      sortable: true,
    },
    {
      key: 'appliesTo',
      label: t('appliesTo.label'),
      width: '12%',
    },
    {
      key: 'tos',
      label: t('tos'),
      width: '8%',
    },
    {
      key: 'action',
      label: '',
      width: '5%',
    },
  ];

  const handleStateSelection = (value) => {
    setPromotionState(() => value);
  };

  const stateOptions = [
    {
      value: PromotionStateEnum.OPERATIONAL,
      label: `${t('operationalStatus')} (${byStateTotal?.operational ?? 0})`,
    },
    {
      value: PromotionStateEnum.DRAFT,
      label: `${t('draftStatus')} (${byStateTotal?.draft ?? 0})`,
    },
    {
      value: PromotionStateEnum.ARCHIVED,
      label: `${t('archivedStatus')} (${byStateTotal?.archived ?? 0})`,
    },
  ];

  return (
    <SectionWrapper aria-labelledby="promotion-title-heading">
      <Text id="promotion-title-heading" as="h2">
        {t('promotions', { ns: 'hotel' })}
      </Text>
      <Stack
        direction="row"
        alignY="center"
        style={{
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(0.75),
        }}
      >
        <KoobInputSegmented
          options={stateOptions}
          value={promotionState}
          onChange={handleStateSelection}
        />
        <div style={{ marginLeft: 'auto' }}>
          <KoobCreateButton to="./new">{t('new')}</KoobCreateButton>
        </div>
      </Stack>
      <PromotionsListTable
        data={promotions?.data}
        loading={isLoading}
        headers={headers}
        pagination={{
          page,
          setPage,
          perPage,
          setPerPage,
          totalCount: Number(promotions?.headers?.xTotalCount ?? 0),
          totalPages: Number(promotions?.headers?.xTotalPages ?? 0),
        }}
      />
    </SectionWrapper>
  );
};

export default PromotionsList;
